import React, { useState, useEffect, useRef, useCallback } from 'react';
import { msalApp } from '../auth.js';
import ReactMarkdown from 'react-markdown';
import './assets/WBCChatBot.css';
import chatBotLogo from './assets/chat_bot_logo.png';
import ChatHistory from './chatbot/ChatHistory.js';
import ConfirmationDialog from './popups/ConfirmationDialog.js';
import { fetchWithAuth } from './api/authapi.js';
import { getApigeeSessionToken, getApigeeBearerToken } from './api/apigeeService.js';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Pie, Scatter } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// Helper functions for column detection
const detectNumericColumns = (data, columns) => {
    if (!data || data.length === 0) return [];
    const sampleRow = data[0];
    
    return columns.filter(col => {
        if (col in sampleRow) {
            const value = sampleRow[col];
            return typeof value === 'number' && value !== null;
        }
        return false;
    });
};

const detectCategoricalColumn = (data, columns) => {
    if (!data || data.length === 0) {
        return columns[0] || '';
    }
    
    const sampleRow = data[0];
    
    // Look for string columns first
    for (const col of columns) {
        if (col in sampleRow) {
            const value = sampleRow[col];
            if (typeof value === 'string') {
                return col;
            }
        }
    }
    
    // Fallback to first column
    return columns[0] || '';
};

const detectTimeColumn = (columns) => {
    const timeKeywords = ['time', 'timestamp', 'date', 'created', 'event_time', 'epoch'];
    
    for (const col of columns) {
        if (timeKeywords.some(keyword => col.toLowerCase().includes(keyword))) {
            return col;
        }
    }
    
    return columns[0] || '';
};

const suggestHistogramBins = (data, columns) => {
    const suggestions = {};
    const dataSize = data.length;
    
    // Use Sturges' rule: bins = log2(n) + 1
    const suggestedBins = dataSize > 0 
        ? Math.max(5, Math.min(50, Math.floor(Math.log2(dataSize) + 1)))
        : 10;
    
    const numericCols = detectNumericColumns(data, columns);
    numericCols.forEach(col => {
        suggestions[col] = suggestedBins;
    });
    
    return suggestions;
};

const generateFormatHints = (chartType, data) => {
    if (!data || data.length === 0) return null;
    
    const columns = Object.keys(data[0]);
    
    switch (chartType) {
        case 'time_series':
        case 'line':
            return {
                x_axis_type: 'datetime',
                suggested_x_column: detectTimeColumn(columns),
                suggested_y_columns: detectNumericColumns(data, columns)
            };
            
        case 'bar':
            return {
                suggested_x_column: detectCategoricalColumn(data, columns),
                suggested_y_column: detectNumericColumns(data, columns)
            };
            
        case 'histogram':
            return {
                suggested_column: detectNumericColumns(data, columns),
                bin_suggestions: suggestHistogramBins(data, columns)
            };
            
        case 'pie':
            return {
                suggested_label_column: detectCategoricalColumn(data, columns),
                suggested_value_column: detectNumericColumns(data, columns)
            };
            
        case 'scatter':
            const numericCols = detectNumericColumns(data, columns);
            return {
                suggested_x_column: numericCols[0] || null,
                suggested_y_column: numericCols[1] || null
            };
            
        default:
            return null;
    }
};

function WBCChatBot({ isOpen, onClose, userName }) {
    const chatApiUrl = process.env.REACT_APP_CHAT_API_URL || 'https://localhost:8443';
    const defaultWebsocketUrl = `${chatApiUrl.replace(/^http/i, 'ws').replace(/\/$/, '')}/api/v1/chat`;

    // UI and Message State
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([]);
    const messagesEndRef = useRef(null);
    
    // Connection & Loading State
    const [connectionState, setConnectionState] = useState('disconnected');
    const [isConnecting, setIsConnecting] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Only for AI "thinking"
    
    // Conversation & Session State
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [historyKey, setHistoryKey] = useState(0);
    const [isNewChat, setIsNewChat] = useState(true); // Track if this is a new chat session
    
    // Chat History State
    const [conversations, setConversations] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    
    // Delete Conversation State
    const [conversationToDelete, setConversationToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Sidebar visibility state
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    // Feedback Modal State
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [feedbackMessageId, setFeedbackMessageId] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');
    
    // Timeout Banner State
    const [timeoutInfo, setTimeoutInfo] = useState(null); // { type, message, timestamp }
    const [showTimeoutBanner, setShowTimeoutBanner] = useState(false);
    
    // Refs for managing WebSocket
    const wsRef = useRef(null);
    const pendingInputRef = useRef(null); // Store input value during reconnection
    
    // Token cache to avoid repeated MSAL calls
    const tokenCacheRef = useRef({ token: null, expiry: null });

    const hasMessages = messages.length > 0;
    
    // Helper function to detect and parse chart data from message text
    const parseChartData = (text) => {
        // Handle object input
        if (typeof text === 'object' && text !== null) {
            if (text.type && text.columns && text.data && Array.isArray(text.data)) {
                return text;
            }
            return null;
        }
        
        if (typeof text !== 'string') return null;
        
        // Only process strings that contain markdown code blocks
        const trimmed = text.trim();
        if (!trimmed.includes('```')) {
            return null; // Not a code block, skip
        }
        
        try {
            // Extract JSON from markdown code block
            const match = trimmed.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
            if (!match) return null;
            
            let jsonStr = match[1].trim();
            
            // Try to parse the JSON - first attempt as-is
            try {
                const parsed = JSON.parse(jsonStr);
                
                // Validate it's chart data
                if (parsed && parsed.type && parsed.columns && parsed.data && Array.isArray(parsed.data)) {
                    console.log('[CHART] Valid chart data found:', parsed.type, 'with', parsed.data.length, 'rows');
                    return parsed;
                }
            } catch (firstError) {
                // If first parse fails, try cleaning the string
                console.log('[CHART] First parse failed, attempting to clean JSON string...');
                
                // Remove any invalid escape sequences (keep only valid ones: " \ / b f n r t u)
                jsonStr = jsonStr.replace(/\\(?!["\\/bfnrtu])/g, '\\\\');
                
                const parsed = JSON.parse(jsonStr);
                
                if (parsed && parsed.type && parsed.columns && parsed.data && Array.isArray(parsed.data)) {
                    console.log('[CHART] Valid chart data found after cleaning:', parsed.type, 'with', parsed.data.length, 'rows');
                    return parsed;
                }
            }
        } catch (e) {
            console.error('[CHART] Failed to parse code block:', e.message);
            return null;
        }
        
        return null;
    };
    
    // Chart component for rendering different chart types
    const ChartComponent = ({ chartData }) => {
        console.log('[CHART] ChartComponent received chartData:', chartData);
        
        // ALWAYS generate format_hints locally (ignore backend format_hints)
        if (chartData && chartData.data) {
            chartData.format_hints = generateFormatHints(chartData.type, chartData.data);
            console.log('[CHART] Generated format_hints:', chartData.format_hints);
        }
        
        // Error message constant
        const CHART_ERROR_MESSAGE = "Unable to display the visualization. Please try rephrasing your request or provide more specific details. If the issue persists, contact 'wbc.server.triage'";

        // Validate chart data before rendering
        const validateChartData = () => {
            if (!chartData) {
                return { valid: false, reason: 'No chart data provided' };
            }

            if (!chartData.data || !Array.isArray(chartData.data) || chartData.data.length === 0) {
                return { valid: false, reason: 'No data available to visualize' };
            }

            if (!chartData.type) {
                return { valid: false, reason: 'Chart type not specified' };
            }

            // Type-specific validation
            const hints = chartData.format_hints;
            switch (chartData.type) {
                case 'pie':
                    if (!hints?.suggested_label_column || !hints?.suggested_value_column) {
                        return { valid: false, reason: 'Missing required column mappings for pie chart' };
                    }
                    break;
                case 'bar':
                    if (!hints?.suggested_x_column || !hints?.suggested_y_column) {
                        return { valid: false, reason: 'Missing required column mappings for chart' };
                    }
                    break;
                case 'line':
                case 'time_series':
                    if (!hints?.suggested_x_column || (!hints?.suggested_y_columns && !hints?.suggested_y_column)) {
                        return { valid: false, reason: 'Missing required column mappings for chart' };
                    }
                    break;
                case 'scatter':
                    if (!hints?.suggested_x_column || !hints?.suggested_y_column) {
                        return { valid: false, reason: 'Missing required column mappings for scatter plot' };
                    }
                    break;
                case 'histogram':
                    if (!hints?.suggested_column) {
                        return { valid: false, reason: 'Missing required column mapping for histogram' };
                    }
                    break;
            }

            return { valid: true };
        };

        const validation = validateChartData();
        console.log('[CHART] Validation result:', validation);
        
        if (!validation.valid) {
            console.error('[CHART] Validation failed:', validation.reason);
            return (
                <div className="wbc-chart-error">
                    <div className="wbc-chart-error-icon">⚠️</div>
                    <div className="wbc-chart-error-message">
                        {CHART_ERROR_MESSAGE}
                    </div>
                </div>
            );
        }

        const getChartConfig = () => {
            if (!chartData) return null;

            const commonOptions = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            boxWidth: 12,
                            padding: 8,
                            font: {
                                size: 10
                            }
                        }
                    },
                    title: {
                        display: false
                    },
                    tooltip: {
                        enabled: true,
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 8,
                        titleFont: {
                            size: 11
                        },
                        bodyFont: {
                            size: 10
                        }
                    }
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                }
            };

            switch (chartData.type) {
                case 'pie':
                    return renderPieChart();
                case 'bar':
                    return renderBarChart(commonOptions);
                case 'line':
                    return renderTimeSeriesChart(commonOptions);
                case 'time_series':
                    return renderTimeSeriesChart(commonOptions);
                case 'scatter':
                    return renderScatterChart(commonOptions);
                case 'histogram':
                    return renderHistogram(commonOptions);
                default:
                    return renderBarChart(commonOptions);
            }
        };

        const renderPieChart = () => {
            const labelColumn = chartData.format_hints?.suggested_label_column;
            const valueColumns = chartData.format_hints?.suggested_value_column;
            const valueColumn = Array.isArray(valueColumns) ? valueColumns[0] : valueColumns;

            const labels = chartData.data.map(item => item[labelColumn] || 'Unknown');
            const values = chartData.data.map(item => item[valueColumn] || 0);

            const data = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: [
                        'rgba(52, 152, 219, 0.8)',
                        'rgba(231, 76, 60, 0.8)',
                        'rgba(46, 204, 113, 0.8)',
                        'rgba(243, 156, 18, 0.8)',
                        'rgba(155, 89, 182, 0.8)',
                        'rgba(26, 188, 156, 0.8)'
                    ],
                    borderColor: [
                        'rgba(52, 152, 219, 1)',
                        'rgba(231, 76, 60, 1)',
                        'rgba(46, 204, 113, 1)',
                        'rgba(243, 156, 18, 1)',
                        'rgba(155, 89, 182, 1)',
                        'rgba(26, 188, 156, 1)'
                    ],
                    borderWidth: 2
                }]
            };

            const options = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            boxWidth: 12,
                            padding: 6,
                            font: {
                                size: 9
                            }
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                const label = context.label || '';
                                const value = context.parsed || 0;
                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                const percentage = ((value / total) * 100).toFixed(1);
                                return `${label}: ${value} (${percentage}%)`;
                            }
                        }
                    }
                }
            };

            return { type: 'pie', data, options };
        };

        const renderBarChart = (commonOptions) => {
            const xColumn = chartData.format_hints?.suggested_x_column;
            const yColumns = chartData.format_hints?.suggested_y_column;
            const yColumn = Array.isArray(yColumns) ? yColumns[0] : yColumns;

            const labels = chartData.data.map(item => {
                const label = item[xColumn] || 'Unknown';
                return label.length > 15 ? label.substring(0, 12) + '...' : label;
            });
            const values = chartData.data.map(item => item[yColumn] || 0);

            const data = {
                labels: labels,
                datasets: [{
                    label: chartData.y_label || yColumn || 'Value',
                    data: values,
                    backgroundColor: 'rgba(52, 152, 219, 0.8)',
                    borderColor: 'rgba(52, 152, 219, 1)',
                    borderWidth: 2
                }]
            };

            const options = {
                ...commonOptions,
                scales: {
                    x: {
                        title: {
                            display: !!chartData.x_label,
                            text: chartData.x_label || '',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 9
                            }
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: !!chartData.y_label,
                            text: chartData.y_label || '',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 9
                            }
                        }
                    }
                }
            };

            return { type: 'bar', data, options };
        };

        const renderTimeSeriesChart = (commonOptions) => {
            const xColumn = chartData.format_hints?.suggested_x_column;
            const yColumns = chartData.format_hints?.suggested_y_columns || chartData.format_hints?.suggested_y_column;
            const yColumn = Array.isArray(yColumns) ? yColumns[0] : yColumns;

            const labels = chartData.data.map(item => item[xColumn] || '');
            const values = chartData.data.map(item => item[yColumn] || 0);

            const data = {
                labels: labels,
                datasets: [{
                    label: chartData.y_label || yColumn || 'Value',
                    data: values,
                    borderColor: 'rgba(52, 152, 219, 1)',
                    backgroundColor: 'rgba(52, 152, 219, 0.1)',
                    borderWidth: 2,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                    pointBackgroundColor: 'rgba(52, 152, 219, 1)',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 1,
                    tension: 0.1,
                    fill: true
                }]
            };

            const options = {
                ...commonOptions,
                scales: {
                    x: {
                        title: {
                            display: !!chartData.x_label,
                            text: chartData.x_label || '',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 8
                            },
                            maxRotation: 45,
                            minRotation: 0
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: !!chartData.y_label,
                            text: chartData.y_label || '',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 9
                            }
                        }
                    }
                }
            };

            return { type: 'line', data, options };
        };

        const renderScatterChart = (commonOptions) => {
            const xColumn = chartData.format_hints?.suggested_x_column;
            const yColumn = chartData.format_hints?.suggested_y_column;

            const scatterData = chartData.data.map(item => ({
                x: item[xColumn] || 0,
                y: item[yColumn] || 0
            }));

            const data = {
                datasets: [{
                    label: 'Data Points',
                    data: scatterData,
                    backgroundColor: 'rgba(52, 152, 219, 0.6)',
                    borderColor: 'rgba(52, 152, 219, 1)',
                    borderWidth: 1,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }]
            };

            const options = {
                ...commonOptions,
                scales: {
                    x: {
                        type: 'linear',
                        position: 'bottom',
                        title: {
                            display: !!chartData.x_label,
                            text: chartData.x_label || xColumn || 'X',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 9
                            }
                        }
                    },
                    y: {
                        title: {
                            display: !!chartData.y_label,
                            text: chartData.y_label || yColumn || 'Y',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 9
                            }
                        }
                    }
                }
            };

            return { type: 'scatter', data, options };
        };

        const renderHistogram = (commonOptions) => {
            const column = chartData.format_hints?.suggested_column;
            const columnToUse = Array.isArray(column) ? column[0] : column;
            const values = chartData.data.map(item => item[columnToUse] || 0);
        
            const binSuggestions = chartData.format_hints?.bin_suggestions || {};
            const bins = binSuggestions[columnToUse] || 10;

            const minValue = Math.min(...values);
            const maxValue = Math.max(...values);
            const binWidth = (maxValue - minValue) / bins;

            const histogram = new Array(bins).fill(0);
            const binLabels = [];

            values.forEach(value => {
                const binIndex = Math.min(Math.floor((value - minValue) / binWidth), bins - 1);
                histogram[binIndex]++;
            });

            for (let i = 0; i < bins; i++) {
                const binStart = (minValue + i * binWidth).toFixed(1);
                const binEnd = (minValue + (i + 1) * binWidth).toFixed(1);
                binLabels.push(`${binStart}-${binEnd}`);
            }

            const data = {
                labels: binLabels,
                datasets: [{
                    label: 'Frequency',
                    data: histogram,
                    backgroundColor: 'rgba(52, 152, 219, 0.8)',
                    borderColor: 'rgba(52, 152, 219, 1)',
                    borderWidth: 2
                }]
            };

            const options = {
                ...commonOptions,
                scales: {
                    x: {
                        title: {
                            display: !!chartData.x_label,
                            text: chartData.x_label || columnToUse || 'Range',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 8
                            },
                            maxRotation: 45,
                            minRotation: 0
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: !!chartData.y_label,
                            text: chartData.y_label || 'Frequency',
                            font: {
                                size: 11,
                                weight: 'bold'
                            }
                        },
                        ticks: {
                            font: {
                                size: 9
                            },
                            precision: 0
                        }
                    }
                }
            };

            return { type: 'bar', data, options };
        };

        let chartConfig;
        try {
            chartConfig = getChartConfig();
        } catch (error) {
            console.error('Error generating chart config:', error);
            return (
                <div className="wbc-chart-error">
                    <div className="wbc-chart-error-icon">⚠️</div>
                    <div className="wbc-chart-error-message">
                        {CHART_ERROR_MESSAGE}
                    </div>
                </div>
            );
        }
        
        if (!chartConfig) {
            return (
                <div className="wbc-chart-error">
                    <div className="wbc-chart-error-icon">⚠️</div>
                    <div className="wbc-chart-error-message">
                        {CHART_ERROR_MESSAGE}
                    </div>
                </div>
            );
        }

        try {
            return (
                <div className="wbc-chart-container">
                    <div className="wbc-chart-title">{chartData.title}</div>
                    <div className="wbc-chart-canvas" style={{ height: '250px', position: 'relative' }}>
                        {chartConfig.type === 'pie' && <Pie data={chartConfig.data} options={chartConfig.options} />}
                        {chartConfig.type === 'bar' && <Bar data={chartConfig.data} options={chartConfig.options} />}
                        {chartConfig.type === 'line' && <Line data={chartConfig.data} options={chartConfig.options} />}
                        {chartConfig.type === 'scatter' && <Scatter data={chartConfig.data} options={chartConfig.options} />}
                    </div>
                    <div className="wbc-chart-metadata">
                        <small>Rows: {chartData.row_count}</small>
                    </div>
                </div>
            );
        } catch (error) {
            console.error('Error rendering chart:', error);
            return (
                <div className="wbc-chart-error">
                    <div className="wbc-chart-error-icon">⚠️</div>
                    <div className="wbc-chart-error-message">
                        {CHART_ERROR_MESSAGE}
                    </div>
                </div>
            );
        }
    };
    
    // Component to render a message (text or chart)
    const MessageContent = ({ message }) => {
        const chartData = parseChartData(message.text);
        
        if (chartData) {
            console.log('[CHART] Chart data detected:', {
                type: chartData.type,
                hasData: !!chartData.data,
                dataLength: chartData.data?.length,
                columns: chartData.columns,
                title: chartData.title
            });
            return <ChartComponent chartData={chartData} />;
        }
        
        return <ReactMarkdown className="wbc-message-text">{message.text}</ReactMarkdown>;
    };
    
    // Get cached MSAL token or fetch new one if expired
    const getToken = useCallback(async () => {
        const now = Date.now();

        // Return cached token if still valid (with 5 min buffer before expiry)
        if (tokenCacheRef.current.token && tokenCacheRef.current.expiry > now + 300000) {
            console.log('[TOKEN] Using cached MSAL token');
            return tokenCacheRef.current.token;
        }

        console.log('[TOKEN] Cache miss — fetching new MSAL token');

        const accounts = msalApp.getAllAccounts();
        console.log('[TOKEN] MSAL accounts available:', accounts.length);
        if (accounts.length === 0) {
            throw new Error('No accounts found. User needs to login first.');
        }
        console.log('[TOKEN] Using account:', accounts[0].username);

        const loginRequest = {
            scopes: ['email', 'openid', 'profile'],
            account: accounts[0]
        };

        let response;
        try {
            response = await msalApp.acquireTokenSilent(loginRequest);
        } catch (err) {
            console.error('[TOKEN] acquireTokenSilent failed:', err);
            throw err;
        }

        tokenCacheRef.current = {
            token: response.accessToken,
            expiry: now + 3600000
        };

        console.log('[TOKEN] New MSAL token acquired and cached');
        return response.accessToken;
    }, []);

    // Scroll to bottom effect
    useEffect(() => {
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }, [messages, isOpen]);

    // Load conversation history when chat opens
    useEffect(() => {
        if (isOpen) {
            loadConversations();
        }
    }, [isOpen, historyKey]);

    // Connect WebSocket when chat opens - reconnect to active conversation if exists
    useEffect(() => {
        if (isOpen) {
            // If there's an active conversation, reconnect to it
            if (currentConversationId) {
                handleSelectConversation(currentConversationId);
            } else {
                // Otherwise start a new chat
                connectWebSocket(null);
            }
        }

        return () => {
            // Don't close WebSocket or clear state when closing chat
            // This preserves the conversation for when user reopens
        };
    }, [isOpen]);

    const loadConversations = async () => {
        try {
            setHistoryLoading(true);
            const data = await fetchWithAuth('/api/v1/conversations');
            setConversations(data || []);
            setHistoryError(null);
        } catch (err) {
            setHistoryError('Failed to load conversations.');
            console.error(err);
        } finally {
            setHistoryLoading(false);
        }
    };

    const loadConversationMessages = async (conversationId) => {
        try {
            console.log('[REST] Fetching messages for conversation:', conversationId);
            const data = await fetchWithAuth(`/api/v1/conversations/${conversationId}/messages`);
            console.log('[REST] Fetched messages:', data);
            
            const historyMessages = (data || []).map(msg => ({
                id: msg.id,
                type: msg.role === 'user' ? 'user' : 'assistant',
                text: msg.content,
                timestamp: new Date(msg.created_at),
                rating: msg.rating || null,
                feedback: msg.feedback || null
            })).reverse(); // Reverse to show oldest first
            
            console.log('[REST] Mapped history messages (reversed):', historyMessages);
            return historyMessages;
        } catch (err) {
            console.error('[REST] Failed to load conversation messages:', err);
            return [];
        }
    };

    const connectWebSocket = useCallback(async (conversationId) => {
        console.log('[WS] Connecting with conversation_id:', conversationId);
        setConnectionState('connecting');
        setIsConnecting(true);
        
        // Only clear messages if starting a new chat (no conversationId)
        if (!conversationId) {
            setMessages([]);
        }

        if (wsRef.current) {
            wsRef.current.close();
        }

        try {
            // Invoke bootstrap-get-cookie before opening the WebSocket connection.
            // The APIGEE session token is fetched and cached here; it is also used
            // as a Cookie header in REST API calls via authapi.js.
            console.log('[WS] Step 1: fetching APIGEE session cookie');
            await getApigeeSessionToken();
            console.log('[WS] Step 1 complete: APIGEE session cookie ready');

            // Use cached MSAL token for WebSocket authentication
            console.log('[WS] Step 2: acquiring MSAL token');
            const token = await getToken();
            console.log('[WS] Step 2 complete: MSAL token acquired');

            const websocketUrl = process.env.REACT_APP_WBC_WEBSOCKET_URL || defaultWebsocketUrl;
            const url = new URL(websocketUrl);
            url.searchParams.append('token', token);
            if (conversationId) {
                url.searchParams.append('conversation_id', conversationId);
            }
            console.log('[WS] WebSocket URL:', url.toString().replace(/token=[^&]+/, 'token=***'));

            const ws = new WebSocket(url.toString());
            wsRef.current = ws;

            ws.onopen = () => {
                const timestamp = new Date().toISOString();
                console.log('[WS] Connection opened successfully at', timestamp);
                setConnectionState('connected');
                setIsConnecting(false);
                
                // Clear any previous timeout banner
                setShowTimeoutBanner(false);
                setTimeoutInfo(null);
                
                // Restore pending input if reconnecting
                if (pendingInputRef.current) {
                    setInputValue(pendingInputRef.current);
                    pendingInputRef.current = null;
                    console.log('[RECONNECT] Restored pending input after reconnection');
                }
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                handleWebSocketMessage(data);
            };

            ws.onerror = (error) => {
                const timestamp = new Date().toISOString();
                console.error('[TIMEOUT] Connection error occurred at', timestamp, error);
                setConnectionState('error');
                setIsConnecting(false);
                
                // Show timeout banner for connection errors
                setTimeoutInfo({
                    type: 'error',
                    message: 'Connection lost unexpectedly',
                    timestamp: new Date()
                });
                setShowTimeoutBanner(true);
            };

            ws.onclose = (event) => {
                const timestamp = new Date().toISOString();
                console.log('[TIMEOUT] WebSocket closed at', timestamp, {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean
                });
                
                setConnectionState('disconnected');
                setIsConnecting(false);
                
                // Determine timeout type and show appropriate banner
                let timeoutType = 'disconnected';
                let timeoutMessage = 'Connection closed';
                
                if (event.code === 1008) {
                    // Policy violation (often auth failure or idle timeout)
                    console.log('[TIMEOUT] Server closed connection - Code 1008 (Policy Violation) at', timestamp);
                    timeoutType = 'server_timeout';
                    timeoutMessage = 'Server closed the connection due to timeout or policy violation';
                } else if (event.code === 1001) {
                    // Going away
                    console.log('[TIMEOUT] Server closed connection - Code 1001 (Going Away) at', timestamp);
                    timeoutType = 'server_timeout';
                    timeoutMessage = 'Server closed the connection';
                } else if (event.code === 1006) {
                    // Abnormal closure
                    console.log('[TIMEOUT] Abnormal connection closure (code 1006) at', timestamp);
                    timeoutType = 'abnormal';
                    timeoutMessage = 'Connection lost unexpectedly';
                } else if (event.code !== 1000) {
                    // Other non-normal closures
                    console.log('[TIMEOUT] Unexpected connection close - Code', event.code, 'at', timestamp);
                    timeoutType = 'error';
                    timeoutMessage = `Connection closed unexpectedly (code ${event.code})`;
                }
                
                // Show timeout banner for non-normal closures
                if (event.code !== 1000) {
                    setTimeoutInfo({
                        type: timeoutType,
                        message: timeoutMessage,
                        timestamp: new Date()
                    });
                    setShowTimeoutBanner(true);
                    
                    // Auto-dismiss banner after 10 seconds
                    setTimeout(() => {
                        setShowTimeoutBanner(false);
                    }, 10000);
                }
            };
        } catch (error) {
            console.error('Connection failed:', error);
            setConnectionState('error');
            setIsConnecting(false);
        }
    }, [getToken, getApigeeSessionToken]);

    function handleWebSocketMessage(data) {
        switch (data.type) {
            case 'connected':
                console.log('[WS] Connected event received:', data);
                
                setConnectionState('connected');
                setCurrentConversationId(data.conversation_id);
                setIsConnecting(false);
                
                // Only show welcome message for new chats (when there are no existing messages)
                setMessages(prev => {
                    if (prev.length === 0) {
                        const welcomeMessage = { id: Date.now(), type: 'system', text: data.message, timestamp: new Date() };
                        return [welcomeMessage];
                    }
                    return prev;
                });
                break;

            case 'server_heartbeat':
                console.log('[HEARTBEAT] Received from server at', data.timestamp);
                // Send acknowledgment back to server
                if (wsRef.current?.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({
                        type: "server_heartbeat_ack",
                        timestamp: new Date().toISOString()
                    }));
                    console.log('[HEARTBEAT] Sent acknowledgment to server');
                } else {
                    console.warn('[HEARTBEAT] Cannot send ack - WebSocket not open');
                }
                break;

            case 'conversation_created':
                setCurrentConversationId(data.conversation_id);
                setHistoryKey(prev => prev + 1);
                break;

            case 'message':
                setMessages(prev => [...prev, { 
                    id: data.message_id || Date.now(), 
                    type: 'assistant', 
                    text: data.content, 
                    timestamp: new Date(),
                    rating: null,
                    feedback: null
                }]);
                setIsLoading(false);
                break;

            case 'tool_start':
                setIsLoading(true);
                break;

            case 'tool_complete':
                break;
            
            case 'complete':
                setIsLoading(false);
                break;

            case 'error':
                setMessages(prev => [...prev, { id: Date.now(), type: 'error', text: data.message, timestamp: new Date() }]);
                setIsLoading(false);
                break;

            default:
                console.warn('Unknown message type:', data.type);
        }
    }

    const handleSendMessage = () => {
        if (!inputValue.trim() || connectionState !== 'connected' || isLoading) return;
    
        const userMessage = { id: Date.now(), type: 'user', text: inputValue, timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
    
        wsRef.current.send(JSON.stringify({ message: inputValue }));
        setInputValue('');
        setIsLoading(true);
        
        // Hide landing page once user sends first message
        if (isNewChat) {
            setIsNewChat(false);
        }
    };

    const handleNewChat = useCallback(() => {
        setCurrentConversationId(null);
        setIsNewChat(true);
        connectWebSocket(null);
    }, [connectWebSocket]);

    const handleReconnect = useCallback(() => {
        console.log('[RECONNECT] Manual reconnect triggered');
        
        // Store current input value before reconnecting
        if (inputValue.trim()) {
            pendingInputRef.current = inputValue;
            console.log('[RECONNECT] Stored pending input for restoration after reconnection');
        }
        
        if (currentConversationId) {
            connectWebSocket(currentConversationId);
        } else {
            connectWebSocket(null);
        }
    }, [currentConversationId, connectWebSocket, inputValue]);

    const handleSelectConversation = useCallback(async (conversationId) => {
        if (conversationId === currentConversationId) return;
        
        console.log('[SELECT] Loading conversation:', conversationId);
        setCurrentConversationId(conversationId);
        setIsNewChat(false); // This is an old conversation
        setIsConnecting(true);
        
        // Fetch conversation history first
        const historyMessages = await loadConversationMessages(conversationId);
        setMessages(historyMessages);
        
        // Scroll to bottom after messages are loaded
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }, 150);
        
        // Then connect WebSocket
        connectWebSocket(conversationId);
    }, [currentConversationId, connectWebSocket]);

    const handleDeleteRequest = useCallback((conversationId) => {
        setConversationToDelete(conversationId);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!conversationToDelete || isDeleting) return;

        try {
            setIsDeleting(true);
            console.log('[DELETE] Deleting conversation:', conversationToDelete);
            
            // Use cached MSAL token as query parameter
            console.log('[DELETE] Step 1: acquiring MSAL token');
            const token = await getToken();
            console.log('[DELETE] Step 1 complete: MSAL token acquired');

            console.log('[DELETE] Step 2: refreshing APIGEE session cookie');
            await getApigeeSessionToken(); // ensure cookie is fresh in browser store
            console.log('[DELETE] Step 2 complete: APIGEE session cookie ready');

            const endpoint = `${chatApiUrl}/api/v1/conversations/${conversationToDelete}?token=${token}`;

            console.log('[DELETE] Step 3: acquiring APIGEE bearer token');
            const deleteHeaders = {};
            const apigeeToken = await getApigeeBearerToken();
            deleteHeaders['Authorization'] = `Bearer ${apigeeToken}`;
            console.log('[DELETE] Step 3 complete: APIGEE bearer token acquired');

            console.log('[DELETE] Step 4: sending DELETE request');
            const response = await fetch(endpoint, {
                method: 'DELETE',
                credentials: 'include', // browser sends apigee_session cookie automatically
                headers: deleteHeaders,
            });
            console.log('[DELETE] Step 4 complete: response status:', response.status);

            // 204 No Content is success for DELETE
            if (response.ok || response.status === 204) {
                console.log('[DELETE] Conversation deleted successfully');
                
                // Reload conversation history from server
                await loadConversations();

                // If we deleted the currently active conversation, start a new chat
                if (conversationToDelete === currentConversationId) {
                    handleNewChat();
                }
            } else {
                throw new Error(`Delete failed with status ${response.status}`);
            }
        } catch (err) {
            console.error('[DELETE] Failed to delete conversation:', err);
            setHistoryError('Failed to delete conversation. Please try again.');
        } finally {
            setIsDeleting(false);
            setConversationToDelete(null);
        }
    }, [conversationToDelete, isDeleting, currentConversationId, handleNewChat, loadConversations, getToken]);

    const handleDeleteCancel = useCallback(() => {
        setConversationToDelete(null);
    }, []);

    // Rating submission function
    const submitRating = async (messageId, rating, feedback = null) => {
        try {
            console.log('[RATING] Step 1: acquiring MSAL token');
            const token = await getToken();
            console.log('[RATING] Step 1 complete: MSAL token acquired');

            console.log('[RATING] Step 2: refreshing APIGEE session cookie');
            await getApigeeSessionToken(); // ensure cookie is fresh in browser store
            console.log('[RATING] Step 2 complete: APIGEE session cookie ready');

            const endpoint = `${chatApiUrl}/api/v1/messages/${messageId}/rating?token=${token}`;

            console.log('[RATING] Submitting rating:', { messageId, rating, feedback });

            console.log('[RATING] Step 3: acquiring APIGEE bearer token');
            const apigeeToken = await getApigeeBearerToken();
            const ratingHeaders = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apigeeToken}`,
            };
            console.log('[RATING] Step 3 complete: APIGEE bearer token acquired');

            console.log('[RATING] Step 4: sending POST request');
            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'include', // browser sends apigee_session cookie automatically
                headers: ratingHeaders,
                body: JSON.stringify({
                    rating: rating,
                    feedback: feedback
                })
            });
            console.log('[RATING] Step 4 complete: response status:', response.status);

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to submit rating');
            }

            const result = await response.json();
            console.log('[RATING] Rating submitted successfully:', result);
            return result;
        } catch (error) {
            console.error('[RATING] Failed to submit rating:', error);
            throw error;
        }
    };

    // Thumbs up handler
    const handleThumbsUp = async (messageId) => {
        try {
            await submitRating(messageId, 'thumbs_up');
            
            // Update local state
            setMessages(prev => prev.map(msg => 
                msg.id === messageId 
                    ? { ...msg, rating: 'thumbs_up', feedback: null }
                    : msg
            ));
        } catch (error) {
            console.error('[RATING] Failed to submit thumbs up');
            // Could show a toast notification here
        }
    };

    // Thumbs down handler - opens feedback modal
    const handleThumbsDown = (messageId) => {
        setFeedbackMessageId(messageId);
        setFeedbackModalOpen(true);
    };

    // Feedback submit handler
    const handleFeedbackSubmit = async () => {
        if (!feedbackMessageId) return;
        
        try {
            await submitRating(feedbackMessageId, 'thumbs_down', feedbackText || null);
            
            // Update local state
            setMessages(prev => prev.map(msg => 
                msg.id === feedbackMessageId 
                    ? { ...msg, rating: 'thumbs_down', feedback: feedbackText }
                    : msg
            ));
            
            // Close modal and reset
            setFeedbackModalOpen(false);
            setFeedbackMessageId(null);
            setFeedbackText('');
        } catch (error) {
            console.error('[RATING] Failed to submit feedback');
            // Keep modal open on error so user can retry
        }
    };

    // Feedback cancel handler
    const handleFeedbackCancel = () => {
        setFeedbackModalOpen(false);
        setFeedbackMessageId(null);
        setFeedbackText('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading && !isConnecting) {
            handleSendMessage();
        }
    };
    
    if (!isOpen) {
        return null;
    }

    const isInputDisabled = connectionState !== 'connected' || isLoading || isConnecting;

    return (
        <>
            <div className="wbc-chat-overlay" onClick={onClose}></div>
            <div className={`wbc-chat-panel ${hasMessages || currentConversationId ? 'wbc-chat-active' : ''}`}>
                {/* Sidebar Toggle Button */}
                <button 
                    className="wbc-sidebar-toggle" 
                    onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                    title={isSidebarVisible ? "Hide sidebar" : "Show sidebar"}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        {isSidebarVisible ? (
                            <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                        ) : (
                            <path fill="currentColor" d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                        )}
                    </svg>
                </button>
                
                <ChatHistory
                    conversations={conversations}
                    isLoading={historyLoading}
                    error={historyError}
                    onSelectConversation={handleSelectConversation}
                    onNewChat={handleNewChat}
                    onDeleteConversation={handleDeleteRequest}
                    activeConversationId={currentConversationId}
                    isSwitching={isConnecting}
                    isVisible={isSidebarVisible}
                    onClose={() => setIsSidebarVisible(false)}
                />
                <div className="wbc-chat-content-wrapper">
                    <div className="wbc-chat-content">
                        <div className="wbc-top-actions">
                            <button className="wbc-close-btn" onClick={onClose}><svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></button>
                        </div>
                        <div className="wbc-chat-header-section">
                            <div className="wbc-chat-logo"><img src={chatBotLogo} alt="WBC Assistant Logo" /></div>
                            <div className="wbc-chat-title-container"><h1 className="wbc-chat-title">WBC Assistant</h1></div>
                        </div>

                        {connectionState === 'error' && (
                             <div className="wbc-disconnection-screen">
                                 <div className="wbc-disconnection-title">Connection Error</div>
                                 <div className="wbc-disconnection-message">Could not connect to the chat service. Please try again.</div>
                                 <button className="wbc-start-new-chat-btn" onClick={handleNewChat}>Try Again</button>
                             </div>
                        )}

                        {connectionState !== 'error' && (
                            <div className="wbc-chat-main-area">
                                {isConnecting ? (
                                    <div className="wbc-chat-messages wbc-loading-placeholder">
                                        <div className="wbc-loading-spinner"></div>
                                        <p>Connecting...</p>
                                    </div>
                                ) : isNewChat && !messages.some(m => m.type === 'user' || m.type === 'assistant') ? (
                                    <div className="wbc-welcome-section">
                                        <div className="wbc-welcome-greeting">Welcome, {userName}! <span className="wbc-wave">👋</span></div>
                                        <div className="wbc-welcome-subtext">I can help you analyze device data, troubleshoot issues, and answer questions about your WBC devices.</div>
                                        
                                        <div className="wbc-help-categories">
                                            <div className="wbc-help-cards-grid">
                                                <div className="wbc-help-card" onClick={() => setInputValue("How are device temperatures looking? Are there any thermal issues?")}>
                                                    <div className="wbc-help-card-icon">🌡️</div>
                                                    <div className="wbc-help-card-content">
                                                        <h3>Thermal Analysis</h3>
                                                        <p>Identify and diagnose device thermal problems</p>
                                                    </div>
                                                </div>
                                                <div className="wbc-help-card" onClick={() => setInputValue("How is network throughput performing across devices?")}>
                                                    <div className="wbc-help-card-icon">📶</div>
                                                    <div className="wbc-help-card-content">
                                                        <h3>Throughput Analysis</h3>
                                                        <p>Monitor and optimize network throughput</p>
                                                    </div>
                                                </div>
                                                <div className="wbc-help-card" onClick={() => setInputValue("Are there any data stall events across my devices?")}>
                                                    <div className="wbc-help-card-icon">⚠️</div>
                                                    <div className="wbc-help-card-content">
                                                        <h3>Data Stall Diagnosis</h3>
                                                        <p>Troubleshoot and resolve data stall problems</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : messages.length > 0 ? (
                                    <div className="wbc-chat-messages">
                                        {/* Timeout Banner */}
                                        {showTimeoutBanner && timeoutInfo && (
                                            <div className="wbc-timeout-banner">
                                                <div className="wbc-timeout-banner-content">
                                                    <div className="wbc-timeout-banner-icon">🔌</div>
                                                    <div className="wbc-timeout-banner-text">
                                                        <div className="wbc-timeout-banner-title">Connection Timeout</div>
                                                        <div className="wbc-timeout-banner-message">
                                                            {timeoutInfo.message} - Click the refresh button to reconnect
                                                        </div>
                                                    </div>
                                                </div>
                                                <button 
                                                    className="wbc-timeout-banner-close"
                                                    onClick={() => setShowTimeoutBanner(false)}
                                                    title="Dismiss"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        )}
                                        {messages.map((message) => (
                                            <div key={message.id} className={`wbc-message ${
                                                message.type === 'user' ? 'wbc-message-user' : 
                                                message.type === 'system' ? 'wbc-message-system' :
                                                message.type === 'chart' ? 'wbc-message-chart' :
                                                'wbc-message-bot'
                                            }`}>
                                                <div className="wbc-message-bubble">
                                                    <MessageContent message={message} />
                                                    {message.type === 'assistant' && (
                                                        <div className="wbc-message-rating">
                                                            <button
                                                                className={`wbc-rating-btn ${message.rating === 'thumbs_up' ? 'wbc-rating-active' : ''}`}
                                                                onClick={() => handleThumbsUp(message.id)}
                                                                disabled={message.rating === 'thumbs_up'}
                                                                title="Helpful"
                                                            >
                                                                👍
                                                            </button>
                                                            <button
                                                                className={`wbc-rating-btn ${message.rating === 'thumbs_down' ? 'wbc-rating-active' : ''}`}
                                                                onClick={() => handleThumbsDown(message.id)}
                                                                disabled={message.rating === 'thumbs_down'}
                                                                title="Not helpful"
                                                            >
                                                                👎
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {isLoading && (
                                            <div className="wbc-message wbc-message-bot">
                                                <div className="wbc-message-bubble wbc-loading-bubble">
                                                    <span className="wbc-loading-dots">Thinking</span>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                ) : (
                                    <div className="wbc-welcome-section">
                                        <div className="wbc-welcome-greeting">Welcome back! <span className="wbc-wave">👋</span></div>
                                        <div className="wbc-welcome-subtext">Select a past conversation or start a new one.</div>
                                    </div>
                                )}
                                
                                <div className="wbc-chat-input-section">
                                    <div className="wbc-input-wrapper">
                                        <input 
                                            type="text" 
                                            className="wbc-chat-input" 
                                            value={inputValue} 
                                            onChange={(e) => setInputValue(e.target.value)} 
                                            onKeyPress={handleKeyPress} 
                                            placeholder={isLoading ? "Waiting for response..." : isConnecting ? "Connecting..." : "Ask me anything..."} 
                                            disabled={isInputDisabled}
                                        />
                                        {connectionState !== 'connected' && !isNewChat && (
                                            <button 
                                                className="wbc-reconnect-button" 
                                                onClick={handleReconnect}
                                                disabled={isConnecting}
                                                title="Reconnect to chat"
                                            >
                                                <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px' }}>
                                                    <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                                                </svg>
                                            </button>
                                        )}
                                        <button 
                                            className="wbc-send-button" 
                                            onClick={handleSendMessage} 
                                            disabled={isInputDisabled || !inputValue.trim()}
                                        >
                                            <svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {conversationToDelete && (
                <ConfirmationDialog
                    message="Are you sure you want to delete this conversation? This action cannot be undone."
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
            )}
            {feedbackModalOpen && (
                <div className="wbc-feedback-modal-overlay" onClick={handleFeedbackCancel}>
                    <div className="wbc-feedback-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="wbc-feedback-modal-header">
                            <h3>Help us improve</h3>
                            <button className="wbc-feedback-modal-close" onClick={handleFeedbackCancel}>
                                ×
                            </button>
                        </div>
                        <div className="wbc-feedback-modal-body">
                            <p>What could be better about this response?</p>
                            <textarea
                                className="wbc-feedback-textarea"
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                placeholder="Optional feedback..."
                                rows="4"
                                autoFocus
                            />
                        </div>
                        <div className="wbc-feedback-modal-footer">
                            <button className="wbc-feedback-btn-cancel" onClick={handleFeedbackCancel}>
                                Cancel
                            </button>
                            <button className="wbc-feedback-btn-submit" onClick={handleFeedbackSubmit}>
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default WBCChatBot;
